import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Form, {
  AsyncRule,
  ButtonItem,
  EmailRule,
  EmptyItem,
  FormRef,
  GroupItem,
  Label,
  PatternRule,
  RequiredRule,
  SimpleItem,
  StringLengthRule,
} from "devextreme-react/form";
import { confirm } from "devextreme/ui/dialog";
import { run, sleep } from "effection";
import sum from "hash-sum";
import { useTranslation } from "react-i18next";

import {
  appNotify,
  abandonHostedOnboarding,
  approveHostedOnboarding,
  cancelGroup,
  createHostedOrganization,
  reserveHostedOnboarding,
  resendNotification,
  retryHostedOnboarding,
  overrideHostedOnboarding,
  selectCurrentGroupsTreeList,
  selectCurrentGroupsTreeListId,
  selectHostedOnboardingById,
  selectedGroupData,
  useAppDispatch,
  useAppSelector,
} from "@app/state";
import { useFxDispatch, useFxSelector } from "@app/statefx";
import { commonStyle } from "@app/style/commonForms";

import type {
  HostedOnboardingRecord,
  HostedOnboardingRequest,
} from "@app/types";

const DEBOUNCE_MS = 250;

const serverUrl =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_SERVERURL_PROD || ""
    : process.env.REACT_APP_SERVERURL_DEV || "";

export const HOSTED_ONBOARDING_PERSEED_PATTERN = /^[A-Z][A-Z0-9]{0,4}$/;

type HostedOnboardingFormData = HostedOnboardingRequest & {
  STATUS?: string;
  CURRENT_STEP?: string;
  LATEST_ERROR?: string;
  LATEST_EVIDENCE?: string;
};

export function OrgNewFormData({ readOnly = false }: { readOnly?: boolean }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const fxDispatch = useFxDispatch();
  const formRef = useRef<FormRef>(null);
  const [formDataSource, setFormDataSource] =
    useState<HostedOnboardingFormData | null>(null);
  const selectedTree = useAppSelector(selectCurrentGroupsTreeList);
  const selectedTreeId = useAppSelector(
    selectCurrentGroupsTreeListId
  ) as number;
  const selectedGroup = useAppSelector((state) =>
    selectedGroupData(state, selectedTreeId)
  );
  const onboarding = useFxSelector((state) =>
    selectHostedOnboardingById(state, selectedTree?.ptlOnboardingId)
  );
  const isDraft = selectedTree?.ptlLifecycle === "DRAFT";
  const isManualReview = onboarding?.status === "MANUAL_REVIEW_REQUIRED";
  const isReadOnly = readOnly || (!isDraft && !isManualReview);
  const commonTextEditOptions = useMemo(
    () => commonStyle({ elementclass: "filledunderline" }),
    []
  );
  const dataCrc = useMemo(
    () => sum({ onboarding, selectedGroup }),
    [onboarding, selectedGroup]
  );

  const eLoadDataset = function* () {
    const request: HostedOnboardingRequest = onboarding?.request || {
      mode: "HOSTED_DATABASE",
      clientCode: selectedGroup?.PERSEED || "",
      organization: {
        legalName: selectedGroup?.GROUP_NAME || "",
        fiscalCode: "",
        tradeRegisterNumber: "",
        billingAddress: "",
        city: "",
        county: "",
        countyCode: "",
        countryCode: "RO",
        billingEmail: "",
        usesRomanianEInvoicing: false,
      },
      owner: {
        email: selectedGroup?.OWNER_EMAIL || "",
        firstName: "",
        lastName: "",
      },
    };
    const latestAttempt = onboarding?.attempts?.at(-1);
    try {
      formRef.current?.instance().beginUpdate();
      setFormDataSource({
        ...request,
        organization: { ...request.organization },
        owner: { ...request.owner },
        STATUS: onboarding?.status || "",
        CURRENT_STEP: onboarding?.currentStep || "",
        LATEST_ERROR: latestAttempt
          ? [latestAttempt.errorCode, latestAttempt.errorMessage]
              .filter(Boolean)
              .join(": ")
          : "",
        LATEST_EVIDENCE: isManualReview
          ? JSON.stringify(
              onboarding?.attempts
                ?.filter((attempt) =>
                  ["CONFLICT", "RETRYABLE_FAILURE"].includes(attempt.result)
                )
                .map((attempt) => ({
                  stepName: attempt.stepName,
                  errorCode: attempt.errorCode,
                  errorMessage: attempt.errorMessage,
                  evidence: attempt.evidence,
                })) || [],
              null,
              2
            )
          : latestAttempt
          ? JSON.stringify(latestAttempt.evidence, null, 2)
          : "",
      });
      yield* sleep(1);
    } finally {
      formRef.current?.instance().endUpdate();
    }
  };

  useEffect(() => {
    run(eLoadDataset);
  }, [dataCrc]);

  const validatePerseedAvailability = useCallback(
    async ({ value }: { value?: string | number }) => {
      if (!isDraft) return true;
      const perseed = `${value || ""}`.trim().toUpperCase();
      if (!HOSTED_ONBOARDING_PERSEED_PATTERN.test(perseed)) return false;
      await sleep(DEBOUNCE_MS);
      const url = `${serverUrl}/rest/api/su/groups/gdcheck?perseed=${encodeURIComponent(
        perseed
      )}`;
      const response = await fetch(url);
      const data = await response.json();
      const availability = data?.[0];
      return Boolean(
        availability?.PERSEED === perseed && availability?.ALLOW_CREATION
      );
    },
    [isDraft]
  );

  const saveClick = useCallback(() => {
    const validation = formRef.current?.instance().validate();
    if (!validation?.isValid) return;
    const formData = formRef.current
      ?.instance()
      .option("formData") as HostedOnboardingFormData;
    if (!formData || !isDraft) return;
    fxDispatch(
      createHostedOrganization({
        mode: "HOSTED_DATABASE",
        clientCode: formData.clientCode.trim().toUpperCase(),
        organization: { ...formData.organization },
        owner: { ...formData.owner },
      })
    );
  }, [fxDispatch, isDraft]);

  const generatePerseed = useCallback(async () => {
    try {
      const perseedEditor = formRef.current?.instance().getEditor("clientCode");
      const current = `${perseedEditor?.option("value") || ""}`.toUpperCase();
      if (current.length >= 5) return;
      const legalName = `${
        formRef.current
          ?.instance()
          .getEditor("organization.legalName")
          ?.option("value") || ""
      }`;
      const prefix = (current || legalName.slice(0, 3)).toUpperCase();
      const url = `${serverUrl}/rest/api/su/groups/gseedsuggest?prefix=${encodeURIComponent(
        prefix
      )}`;
      const response = await fetch(url);
      const data = await response.json();
      const suggestion = data?.[0]?.SUGGESTED;
      if (suggestion) perseedEditor?.option("value", suggestion);
    } catch (_error) {
      dispatch(
        appNotify({
          type: "error",
          message: "Error generating perseed",
          duration: 3000,
        })
      );
    }
  }, [dispatch]);

  const latestRecord = onboarding as HostedOnboardingRecord | null;

  return (
    <div id="orgdata-new">
      <Form
        ref={formRef}
        id="formformOrgData"
        className="form-form org-data"
        formData={formDataSource}
        showColonAfterLabel={true}
        labelLocation="left"
        colCount={1}
        width="100%"
        readOnly={isReadOnly}
        showValidationSummary={true}
        validationGroup="createGroupForm"
        onFieldDataChanged={(event) => {
          if (
            event.dataField !== "clientCode" ||
            (!isDraft && (!isManualReview || onboarding?.perseedLocked))
          )
            return;
          const normalized = `${event.value || ""}`.toUpperCase();
          if (normalized !== event.value) {
            formRef.current
              ?.instance()
              .getEditor("clientCode")
              ?.option("value", normalized);
          }
        }}
      >
        <GroupItem caption={t("Organization Data")} colCount={4}>
          <SimpleItem
            dataField="organization.legalName"
            editorType="dxTextBox"
            editorOptions={{ ...commonTextEditOptions, maxLength: 202 }}
            colSpan={4}
          >
            <Label text={t("GroupName")} />
            <RequiredRule message={t("GroupNameRequiredMessage")} />
            <StringLengthRule min={2} max={202} />
          </SimpleItem>
          <SimpleItem
            dataField="clientCode"
            editorType="dxTextBox"
            editorOptions={{
              ...commonTextEditOptions,
              readOnly:
                !isDraft && (!isManualReview || onboarding?.perseedLocked),
              maxLength: 5,
              hint: t("PerseedMaskInvalidMessage"),
            }}
            colSpan={2}
          >
            <Label text={t("Perseed")} />
            <RequiredRule message={t("PerseedRequiredMessage")} />
            <PatternRule
              pattern={HOSTED_ONBOARDING_PERSEED_PATTERN}
              message={t("PerseedMaskInvalidMessage")}
            />
            <AsyncRule
              message={t("PerseedInvalidMessage")}
              validationCallback={validatePerseedAvailability}
            />
          </SimpleItem>
          <ButtonItem
            colSpan={1}
            horizontalAlignment="left"
            buttonOptions={{
              elementAttr: { class: "generatePerseedButton" },
              icon: "key",
              text: t("GeneratePerseed"),
              visible: isDraft && !readOnly,
              onClick: generatePerseed,
            }}
          />
          <EmptyItem />
          <SimpleItem
            dataField="organization.fiscalCode"
            editorType="dxTextBox"
            editorOptions={{ ...commonTextEditOptions, maxLength: 32 }}
            colSpan={2}
          >
            <Label text="CIF/CUI" />
            <RequiredRule />
            <PatternRule pattern={/^[A-Za-z0-9][A-Za-z0-9 ./-]{1,31}$/} />
          </SimpleItem>
          <SimpleItem
            dataField="organization.tradeRegisterNumber"
            editorType="dxTextBox"
            editorOptions={{ ...commonTextEditOptions, maxLength: 20 }}
            colSpan={2}
          >
            <Label text="Nr. registrul comerțului" />
            <RequiredRule />
            <PatternRule pattern={/^[A-Za-z0-9][A-Za-z0-9 ./-]{1,19}$/} />
          </SimpleItem>
          <SimpleItem
            dataField="organization.billingAddress"
            editorType="dxTextBox"
            editorOptions={{ ...commonTextEditOptions, maxLength: 250 }}
            colSpan={4}
          >
            <Label text="Adresa de facturare" />
            <RequiredRule />
            <StringLengthRule min={5} max={250} />
          </SimpleItem>
          <SimpleItem dataField="organization.city" colSpan={2}>
            <Label text="Localitate" />
            <RequiredRule />
            <StringLengthRule min={2} max={100} />
          </SimpleItem>
          <SimpleItem dataField="organization.county" colSpan={2}>
            <Label text="Județ" />
            <RequiredRule />
            <StringLengthRule min={2} max={100} />
          </SimpleItem>
          <SimpleItem dataField="organization.countyCode" colSpan={2}>
            <Label text="Cod județ" />
            <RequiredRule />
            <PatternRule pattern={/^[A-Za-z0-9-]{1,10}$/} />
          </SimpleItem>
          <SimpleItem dataField="organization.countryCode" colSpan={2}>
            <Label text="Cod țară" />
            <RequiredRule />
            <PatternRule pattern={/^[A-Za-z]{2}$/} />
          </SimpleItem>
          <SimpleItem dataField="organization.billingEmail" colSpan={2}>
            <Label text="Email facturare" />
            <RequiredRule />
            <EmailRule />
          </SimpleItem>
          <SimpleItem
            dataField="organization.usesRomanianEInvoicing"
            editorType="dxCheckBox"
            colSpan={2}
          >
            <Label text="Folosește RO e-Factura" />
          </SimpleItem>
        </GroupItem>

        <GroupItem caption="Proprietar" colCount={4}>
          <SimpleItem
            dataField="owner.firstName"
            editorOptions={{ readOnly: isManualReview }}
            colSpan={2}
          >
            <Label text="Prenume" />
            <RequiredRule />
            <StringLengthRule max={100} />
          </SimpleItem>
          <SimpleItem
            dataField="owner.lastName"
            editorOptions={{ readOnly: isManualReview }}
            colSpan={2}
          >
            <Label text="Nume" />
            <RequiredRule />
            <StringLengthRule max={100} />
          </SimpleItem>
          <SimpleItem
            dataField="owner.email"
            editorOptions={{ readOnly: isManualReview }}
            colSpan={4}
          >
            <Label text="Email proprietar" />
            <RequiredRule />
            <EmailRule />
          </SimpleItem>
        </GroupItem>

        <GroupItem
          caption="Stare onboarding"
          colCount={4}
          visible={!!onboarding}
        >
          <SimpleItem
            dataField="STATUS"
            editorOptions={{ readOnly: true }}
            colSpan={2}
          />
          <SimpleItem
            dataField="CURRENT_STEP"
            editorOptions={{ readOnly: true }}
            colSpan={2}
          />
          <SimpleItem
            dataField="LATEST_ERROR"
            editorOptions={{ readOnly: true }}
            colSpan={4}
          />
          <SimpleItem
            dataField="LATEST_EVIDENCE"
            editorType="dxTextArea"
            editorOptions={{ readOnly: true, minHeight: 140 }}
            colSpan={4}
          >
            <Label
              text={
                isManualReview
                  ? "Conflict evidence and matching records"
                  : "Latest evidence"
              }
          />
          </SimpleItem>
          <ButtonItem
            horizontalAlignment="left"
            buttonOptions={{
              text: "Reserve",
              visible: latestRecord?.status === "DRAFT",
              onClick: () => {
                if (latestRecord) {
                  fxDispatch(
                    reserveHostedOnboarding({
                      id: latestRecord.id,
                    })
                  );
                }
              },
            }}
          />
          <ButtonItem
            horizontalAlignment="left"
            buttonOptions={{
              text: "Approve & Resume",
              type: "success",
              visible: isManualReview,
              onClick: () => {
                if (latestRecord) {
                  fxDispatch(approveHostedOnboarding({ id: latestRecord.id }));
                }
              },
            }}
          />
          <ButtonItem
            horizontalAlignment="left"
            buttonOptions={{
              text: "Override Data",
              visible: isManualReview,
              onClick: () => {
                const validation = formRef.current?.instance().validate();
                if (!validation?.isValid || !latestRecord) return;
                const formData = formRef.current
                  ?.instance()
                  .option("formData") as HostedOnboardingFormData;
                fxDispatch(
                  overrideHostedOnboarding({
                    id: latestRecord.id,
                    clientCode: formData.clientCode.trim().toUpperCase(),
                    organization: { ...formData.organization },
                  })
                );
              },
            }}
          />
          <ButtonItem
            horizontalAlignment="left"
            buttonOptions={{
              text: "Abandon & Refund",
              type: "danger",
              visible: isManualReview,
              onClick: async () => {
                if (
                  !latestRecord ||
                  !(await confirm(
                    "Abandon this onboarding and trigger any applicable refund?",
                    "Confirm abandonment"
                  ))
                )
                  return;
                fxDispatch(
                  abandonHostedOnboarding({
                    id: latestRecord.id,
                    reason: "Operator abandoned after manual review",
                  })
                );
              },
            }}
          />
          <ButtonItem
            horizontalAlignment="left"
            buttonOptions={{
              text: "Retry",
              visible: latestRecord?.status === "FAILED_RETRYABLE",
              onClick: () => {
                if (latestRecord) {
                  fxDispatch(retryHostedOnboarding({ id: latestRecord.id }));
                }
              },
            }}
          />
          <ButtonItem
            horizontalAlignment="left"
            buttonOptions={{
              text: "Resend Notification",
              visible: latestRecord?.status === "SERVER_READY",
              onClick: () => {
                if (latestRecord) {
                  fxDispatch(resendNotification({ id: latestRecord.id }));
                }
              },
            }}
          />
        </GroupItem>

        <GroupItem caption="Editare" colCount={4} visible={isDraft}>
          <EmptyItem />
          <ButtonItem
            horizontalAlignment="left"
            buttonOptions={{
              elementAttr: { class: "cancelButton" },
              icon: "close",
              text: t("Cancel"),
              onClick: () => dispatch(cancelGroup()),
            }}
          />
          <ButtonItem
            horizontalAlignment="left"
            buttonOptions={{
              elementAttr: { class: "saveButton" },
              icon: "save",
              text: t("YouSave"),
              onClick: saveClick,
              useSubmitBehavior: false,
            }}
          />
          <EmptyItem />
        </GroupItem>
      </Form>
    </div>
  );
}
